// Kleine, wiederverwendbare UI-Bausteine im Blackfort-CI (Navy-Flächen, sparsames Gold).
// Bewusst schlank gehalten; kein Design-System-Overhead.

import type { JSX, ReactNode } from 'react';

// Sektionsüberschrift in einem Panel (Palette, Eigenschaften).
export function PanelHeading({ children }: { children: ReactNode }): JSX.Element {
  return (
    <h3 className="mb-2 text-xs font-semibold uppercase tracking-wider text-bf-muted">
      {children}
    </h3>
  );
}

// Leerer Zustand mit Hinweistext — freundlich statt einer leeren Fläche.
export function EmptyState({
  title,
  hint,
  action,
}: {
  title: string;
  hint?: string;
  // Optionaler Ausweg aus dem leeren Zustand — eine Schaltfläche, die das Fehlende anlegt.
  action?: ReactNode;
}): JSX.Element {
  return (
    <div className="flex h-full min-h-32 flex-col items-center justify-center gap-1 rounded border border-dashed border-bf-border p-6 text-center">
      <p className="text-sm font-medium text-bf-white">{title}</p>
      {hint && <p className="max-w-xs text-xs text-bf-muted">{hint}</p>}
      {action && <div className="mt-3">{action}</div>}
    </div>
  );
}

// Beschriftetes Textfeld für die Eigenschaften-Panels.
export function LabeledInput({
  label,
  value,
  onChange,
  placeholder,
  type = 'text',
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  // 'password' maskiert die Eingabe — nötig für Zugangsschlüssel, die nicht über die Schulter
  // mitlesbar sein sollen (Issue #359).
  type?: 'text' | 'number' | 'password';
  testId?: string;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-bf-muted">{label}</span>
      <input
        type={type}
        value={value}
        placeholder={placeholder}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
      />
    </label>
  );
}

// Beschriftetes Auswahlfeld.
export function LabeledSelect({
  label,
  value,
  onChange,
  children,
  testId,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  children: ReactNode;
  testId?: string;
}): JSX.Element {
  return (
    <label className="block">
      <span className="mb-1 block text-xs text-bf-muted">{label}</span>
      <select
        value={value}
        data-testid={testId}
        onChange={(e) => onChange(e.target.value)}
        className="w-full rounded border border-bf-border bg-bf-navy px-2 py-1.5 text-sm text-bf-white outline-none focus:border-bf-gold"
      >
        {children}
      </select>
    </label>
  );
}

// Primäre (Gold) und sekundäre (Umriss) Schaltfläche.
export function Button({
  onClick,
  children,
  variant = 'secondary',
  disabled = false,
  title,
  testId,
}: {
  onClick: () => void;
  children: ReactNode;
  variant?: 'primary' | 'secondary' | 'danger';
  disabled?: boolean;
  title?: string;
  testId?: string;
}): JSX.Element {
  const base =
    'rounded px-3 py-1.5 text-sm font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-40';
  const styles: Record<string, string> = {
    primary: 'bg-bf-gold text-bf-navy hover:bg-bf-gold-light',
    secondary: 'border border-bf-border text-bf-white hover:border-bf-gold',
    danger: 'border border-red-500/60 text-red-300 hover:border-red-400',
  };
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      data-testid={testId}
      className={`${base} ${styles[variant]}`}
    >
      {children}
    </button>
  );
}
